import { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  CustomAutoComplete,
  MaterialQuantityTable,
  renderTwoLineOption,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS } from '../../../common/styles';
import { useUnits } from '../../../hooks/useUnits';
import { useInventoryArticleSearch } from '../hooks/useInventoryArticleSearch';
import { INTERNAL_ORDER_MATERIAL_FORM_TEXTS as TEXTS } from '../constants/internalOrdersConstants';
import { toMaterialGroupOptions } from '../../utils/toMaterialGroupOptions';

/**
 * Materiales del pedido técnico: grupos de materiales, búsqueda de artículos sueltos y tabla
 * editable de cantidades.
 * @param {Object} props
 * @param {Array<Object>} props.groupOptions Grupos de materiales encontrados.
 * @param {string} props.groupSearch Texto de búsqueda de grupos.
 * @param {Function} props.setGroupSearch Actualiza la búsqueda de grupos.
 * @param {Function} props.changeGroups Recibe los UUID de los grupos elegidos.
 * @param {Array<Object>} props.materials Renglones del pedido.
 * @param {boolean} props.searchEnabled Habilita la búsqueda y la edición.
 * @param {Function} props.onAddMaterial Agrega un artículo suelto.
 * @param {Function} props.onUpdateMaterialQuantity Recibe (clave del renglón, cantidad).
 * @param {Function} props.onRemoveMaterial Recibe la clave del renglón a quitar.
 * @returns {JSX.Element}
 */
const InternalOrderMaterialsSection = ({
  groupOptions = [],
  groupSearch,
  setGroupSearch,
  changeGroups,
  materials = [],
  searchEnabled = true,
  onAddMaterial,
  onUpdateMaterialQuantity,
  onRemoveMaterial,
}) => {
  const { data: units = [] } = useUnits({ enabled: searchEnabled });
  const selectedGroups = [
    ...new Map(
      materials
        .filter((material) => material.uuid_material_group)
        .map((material) => [
          material.uuid_material_group,
          { value: material.uuid_material_group, label: material.group_name },
        ])
    ).values(),
  ];
  const {
    searchTerm,
    setSearchTerm,
    materialOptions,
    isSearching,
    isPendingDebounce,
    hasSearchError,
    resetSearch,
    minSearchLength,
  } = useInventoryArticleSearch(units, searchEnabled);

  const addedMaterialIds = useMemo(
    () => new Set(materials.map((item) => item.materialId)),
    [materials]
  );

  const availableOptions = useMemo(() => {
    if (isPendingDebounce) return [];
    return materialOptions.filter((option) => !addedMaterialIds.has(option.value));
  }, [materialOptions, addedMaterialIds, isPendingDebounce]);

  const handleMaterialSelect = (_, option) => {
    if (!option?.value) return;
    onAddMaterial(option);
    resetSearch();
  };

  const trimmedSearch = searchTerm.trim();

  const noOptionsText = isPendingDebounce
    ? TEXTS.waitTyping
    : trimmedSearch.length > 0 && trimmedSearch.length < minSearchLength
      ? TEXTS.minLength(minSearchLength)
      : isSearching
        ? TEXTS.searching
        : hasSearchError
          ? TEXTS.searchError
          : availableOptions.length === 0 && materials.length > 0
            ? TEXTS.alreadyAdded
            : trimmedSearch.length >= minSearchLength
              ? TEXTS.noResults
              : TEXTS.hint;

  const totalUnits = materials.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="subtitle1" fontWeight={700}>
        {TEXTS.title}
      </Typography>

      <CustomAutoComplete
        label={TEXTS.groups}
        inputPlaceholder={TEXTS.searchGroup}
        multiple
        filterSelectedOptions
        value={selectedGroups}
        inputValue={groupSearch}
        options={toMaterialGroupOptions(groupOptions)}
        renderOption={renderTwoLineOption}
        onInputChange={(_, value, reason) => {
          if (reason !== 'reset') setGroupSearch(value);
        }}
        onChange={(event) => changeGroups(event.target.value)}
        filterOptions={(options) => options}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        sx={{ mt: 0 }}
      />

      <CustomAutoComplete
        label={TEXTS.search}
        inputPlaceholder={TEXTS.searchPlaceholder}
        name="material_picker"
        options={availableOptions}
        value={null}
        inputValue={searchTerm}
        onInputChange={(_, value, reason) => {
          if (reason !== 'reset') setSearchTerm(value);
        }}
        onChange={handleMaterialSelect}
        loading={isSearching}
        disabled={!searchEnabled}
        filterOptions={(options) => options}
        renderOption={renderTwoLineOption}
        ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
        noOptionsText={noOptionsText}
        sx={{ mt: 0 }}
      />

      <MaterialQuantityTable
        rows={materials.map((item) => ({
          key: item.lineId || item.materialId,
          code: item.code,
          name: item.name,
          detail: item.group_name,
          unit: item.unit,
          quantity: item.quantity,
        }))}
        onQuantityChange={onUpdateMaterialQuantity}
        onRemove={onRemoveMaterial}
        emptyMessage={TEXTS.empty}
        disabled={!searchEnabled}
      />

      {materials.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
          {TEXTS.summary(materials.length, totalUnits)}
        </Typography>
      )}
    </Box>
  );
};

export default InternalOrderMaterialsSection;
